import React from 'react';
import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import {
  useSupportCaseThreads,
  useSupportCaseMessages,
  useCreateCaseMessage,
  useUploadAttachment,
} from '../../data/support/useSupportCustomer';
import { SupportApi } from '../../data/support/supportApi';
import { CaseThread, CaseMessageAttachment } from '../../types/support.types';
import CaseMessage from '../internal/support/CaseMessage';
import CaseMessageComposer from '../internal/support/CaseMessageComposer';

interface SupportCaseChatProps {
  caseId: string;
}

const SupportCaseChat: React.FC<SupportCaseChatProps> = ({ caseId }) => {
  const threadsQuery = useSupportCaseThreads(caseId);
  const threads = threadsQuery.data || [];
  const [selectedThreadId, setSelectedThreadId] = React.useState<string>('');

  // Auto-select first thread
  React.useEffect(() => {
    if (threads.length > 0 && !selectedThreadId) {
      setSelectedThreadId(threads[0].id);
    }
  }, [threads, selectedThreadId]);

  if (threadsQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!threads.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No conversation threads yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Thread selector */}
      {threads.length > 1 && (
        <Tabs
          value={threads.findIndex((t) => t.id === selectedThreadId)}
          onChange={(_e, idx) => setSelectedThreadId(threads[idx].id)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 36, mb: 1 }}
        >
          {threads.map((thread: CaseThread) => (
            <Tab
              key={thread.id}
              label={thread.label}
              sx={{ minHeight: 36, py: 0.5, textTransform: 'none' }}
            />
          ))}
        </Tabs>
      )}

      {selectedThreadId && (
        <CustomerThreadView caseId={caseId} threadId={selectedThreadId} />
      )}
    </Box>
  );
};

// Inline thread view for customer (uses customer hooks)
const CustomerThreadView: React.FC<{ caseId: string; threadId: string }> = ({
  caseId,
  threadId,
}) => {
  const params = useParams();
  const accountId = params['accountId'];
  const messagesQuery = useSupportCaseMessages(caseId, threadId, undefined, {
    polling: true,
  });
  const messages = messagesQuery.data || [];
  const createMessage = useCreateCaseMessage(caseId, threadId);
  const uploadAttachment = useUploadAttachment(caseId, threadId);
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async (body: Record<string, any>, files?: File[]) => {
    const result = await createMessage.mutateAsync({ body });
    if (files?.length && result?.id) {
      await Promise.all(
        files.map((file) =>
          uploadAttachment.mutateAsync({ messageId: result.id, file }),
        ),
      );
    }
  };

  const handleDownloadAttachment = async (
    messageId: string,
    attachment: CaseMessageAttachment,
  ) => {
    if (!accountId) return;
    const result = await SupportApi.getAttachmentUrl(
      accountId,
      caseId,
      messageId,
      attachment.id,
    );
    window.open(result.data.attributes.download_url, '_blank');
  };

  if (messagesQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 300,
          maxHeight: 500,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          py: 2,
          px: 1,
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No messages yet</Typography>
          </Box>
        )}
        {messages.map((msg) => (
          <CaseMessage
            key={msg.id}
            message={msg}
            align={
              msg.author_type === 'system'
                ? 'center'
                : msg.author_type === 'customer'
                  ? 'right'
                  : 'left'
            }
            onDownloadAttachment={(att) =>
              handleDownloadAttachment(msg.id, att)
            }
            getAttachmentUrl={
              accountId
                ? async (att) => {
                    const result = await SupportApi.getAttachmentUrl(
                      accountId,
                      caseId,
                      msg.id,
                      att.id,
                    );
                    return result.data.attributes.download_url;
                  }
                : undefined
            }
          />
        ))}
        <div ref={bottomRef} />
      </Box>

      <Box sx={{ mt: 2 }}>
        <CaseMessageComposer
          onSend={handleSend}
          isPending={createMessage.isPending || uploadAttachment.isPending}
          placeholder="Type a message..."
        />
      </Box>
    </>
  );
};

export default SupportCaseChat;
